import type { GPUProduct } from '@/lib/data/gpu-products';

/**
 * GPU classes for the arcade's class bar. Each class matches the real
 * `useCases` strings in the catalogue (lib/data/gpu-products.ts), so a GPU
 * shows under a class only when the catalogue lists that kind of work for it.
 */

export type ArcadeClassId = 'all' | 'gaming' | 'creative' | 'ai' | 'heavy';

/** Electric cyan: the mint chart hue pulled toward lavender. */
export const CYAN = 'color-mix(in oklab, var(--chart-3) 60%, var(--accent))';

export interface ArcadeClass {
  id: ArcadeClassId;
  label: string;
  /** A CSS colour for --hue. */
  hue: string;
  /** Catalogue use cases that put a GPU in this class; null = every GPU. */
  useCases: readonly string[] | null;
}

export const ARCADE_CLASSES: readonly ArcadeClass[] = [
  { id: 'all', label: 'All Power', hue: 'var(--primary)', useCases: null },
  { id: 'gaming', label: 'Gaming', hue: 'var(--chart-4)', useCases: ['Gaming', 'AAA Gaming', 'Streaming'] },
  {
    id: 'creative',
    label: 'Creative',
    hue: 'var(--chart-5)',
    useCases: ['Content Creation', 'Rendering', 'Professional Rendering'],
  },
  { id: 'ai', label: 'AI & ML', hue: CYAN, useCases: ['Light ML', 'AI Inference', 'ML Training'] },
  {
    id: 'heavy',
    label: 'Heavy Compute',
    hue: 'var(--accent)',
    useCases: ['Heavy Multitasking', 'ML Training', 'Professional Rendering'],
  },
];

export function classById(id: ArcadeClassId): ArcadeClass {
  return ARCADE_CLASSES.find((c) => c.id === id) ?? ARCADE_CLASSES[0];
}

export function inClass(gpu: GPUProduct, id: ArcadeClassId): boolean {
  const cls = classById(id);
  return cls.useCases === null || gpu.useCases.some((useCase) => cls.useCases!.includes(useCase));
}

/** The hue of the first class a use case belongs to, for tinting its tag. */
export function useCaseHue(useCase: string): string {
  return ARCADE_CLASSES.find((c) => c.useCases?.includes(useCase))?.hue ?? 'var(--muted-foreground)';
}

export type SortId = 'featured' | 'price-asc' | 'price-desc' | 'vram-desc';

export const SORTS: readonly { id: SortId; label: string }[] = [
  { id: 'featured', label: 'Arcade order' },
  { id: 'price-asc', label: 'Price: low to high' },
  { id: 'price-desc', label: 'Price: high to low' },
  { id: 'vram-desc', label: 'Most VRAM' },
];

export function sortGpus(list: GPUProduct[], sort: SortId, price: (gpu: GPUProduct) => number): GPUProduct[] {
  if (sort === 'featured') return list;
  const sorted = [...list];
  if (sort === 'price-asc') sorted.sort((a, b) => price(a) - price(b));
  if (sort === 'price-desc') sorted.sort((a, b) => price(b) - price(a));
  if (sort === 'vram-desc') sorted.sort((a, b) => b.vram - a.vram || price(a) - price(b));
  // Something nobody can rent always goes last, whatever the order.
  return sorted.sort((a, b) => Number(b.available) - Number(a.available));
}
